import { Toast, useToasts } from '@tamagui/toast/v2';
import { Text } from 'tamagui';

import { space } from '@/constants/tokens';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PSCircleButton from './PSCircleButton';
import PSIcon from './PSIcon';

/**
 * PSToastHost — where `toast()` messages actually render.
 *
 * Toasts are fired straight from call sites with @tamagui/toast's v2 imperative
 * api (`toast.error('…')`), which publishes to a module-level observer — that's
 * why a catch block can raise one with no hook and no provider in scope. This
 * component is the other half: the subscriber that draws them.
 *
 * ── Two hosts, never both mounted ──
 * There are exactly two: this one in `app/_layout.tsx` (covering the whole app)
 * and one in `app/auth/_layout.tsx`. The auth flow needs its own because on iOS
 * it's a natively-presented sheet (`presentation: 'modal'`) and the root host
 * renders UNDERNEATH it — every toast in the flow would be invisible.
 *
 * They can't both be mounted, though. The imperative api publishes to ONE
 * module-level observer and every mounted host subscribes to it, so on web and
 * Android — where the modal is NOT a separate native view — both would draw the
 * same toast and the user reads it twice. The root layout therefore mounts its
 * host only when the auth group isn't the active route (`useSegments()`), which
 * makes them mutually exclusive on every platform without assuming anything
 * about how each one presents a modal.
 *
 * A third host would break that invariant. If some other natively-presented
 * screen ever needs its own, extend the SAME gate rather than adding one
 * unconditionally.
 *
 * ── Three things about the composition below ──
 *
 * 1. We use the COMPOSABLE api (`Toast` + `Toast.Viewport` + `Toast.List`), not
 *    the drop-in `<Toaster />`, for one prop: `portalToRoot`. It defaults to
 *    true, which portals the viewport to Tamagui's root portal host — putting it
 *    right back under the modal this host exists to get above. `false` keeps it
 *    rendering in place.
 *
 * 2. Items are `unstyled` and restyled in our tokens. Tamagui's default toast
 *    skin is written against the preset's scales (`$4` padding, `$6` radius,
 *    `$color11` text); this repo replaces those token groups wholesale (see
 *    constants/tokens.ts), so the defaults would resolve to nothing and render
 *    an unpadded, square, invisible-text box.
 *
 * 3. Top, not bottom. Every screen that toasts today is a form with the keyboard
 *    up (mobile, OTP, details) — an absolutely-positioned bottom toast sits
 *    behind the keyboard, which is the one place it can't be read.
 */

// Clears the auth stack's native header, which this host is positioned over (the
// viewport is a sibling of the whole nested <Stack>, so `top: 0` is the top of
// the header, not of the screen body). 64 is comfortably past both platforms'
// header height — 44 on an iOS sheet, 56 on Android — so the ← back button stays
// tappable while a toast is up.
// const HEADER_CLEARANCE = space.xxl * 2;

export default function PSToastHost() {
  const { top: safeAreaTop } = useSafeAreaInsets();
  return (
    <Toast position="top-center" theme="light">
      <PSToastViewport topInset={safeAreaTop} />
    </Toast>
  );
}

// The viewport is a full-screen absolutely-positioned box (so toasts can float
// above whatever's on screen) that stays mounted, "invisible", between toasts —
// on Android its touch pass-through is unreliable enough to swallow taps on
// whatever's underneath. Unmounting it entirely while there's nothing to show
// sidesteps that; `toasts` only empties once a toast's exit animation has
// finished, so this doesn't cut anything short.
function PSToastViewport({ topInset }: { topInset: number }) {
  const { toasts } = useToasts();
  if (toasts.length === 0) return null;

  return (
    <Toast.Viewport
      portalToRoot={false}
      offset={{
        top: topInset,
        left: space.lg,
        right: space.lg,
      }}
    >
      <Toast.List
        renderItem={({ toast, index }) => (
          <Toast.Item
            toast={toast}
            index={index}
            unstyled
            // The only type call sites raise today is `error`, and it's the
            // only one drawn differently — a red card, the same `$error` the
            // invalid-field border uses, so a failure reads the same wherever
            // it lands. `toast.success`/`info`/`warning` all fall through to
            // the neutral dark card until a call site needs otherwise; give
            // them their own look here when one does.
            backgroundColor={toast.type === 'error' ? '$error' : '$textDark'}
            flexDirection="row"
            items="center"
            justify="space-between"
            gap="$sm"
            pl="$lg"
            pr="$sm"
            py="$md"
            rounded="$base"
          >
            <Text color="$textWhite" fontSize="$small" flex={1}>
              {/* Every call site passes a string, so the ReactNode-or-thunk
                  that ToastT allows for `title` never turns up here. */}
              {toast.title as string}
            </Text>
            {/* `bare`: the toast card is itself the surface, same reasoning
                PSCircleButton documents for header placement. */}
            <Toast.Close asChild>
              <PSCircleButton variant="bare">
                <PSIcon name="close-outline" color="$textWhite" size={18} />
              </PSCircleButton>
            </Toast.Close>
          </Toast.Item>
        )}
      />
    </Toast.Viewport>
  );
}
